import {
    AutocompleteInput,
    Breadcrumb,
    DateInput,
    Edit,
    NumberInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    TextInput,
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Landmark, ReceiptText, Truck, WalletCards } from "lucide-react";
import { useRecordContext } from 'ra-core';
import { Link } from 'react-router-dom';
import { DocumentItemsTable } from '../shared/DocumentItemsTable';
import { GrandTotalsWatcher } from '../shared/GrandTotalsWatcher';
import { PaymentTermsWatcher } from '../shared/PaymentTermsWatcher';
import { RevisedReferenceWatcher } from '../shared/RevisedReferenceWatcher';
import { StatusSelectInput } from '../shared/StatusSelectInput';
import { TermsTemplateSelector } from '../shared/TermsTemplateSelector';
import { requireNonZeroTotal } from '../shared/validators';
import { FilteredContactInput } from '../../misc/FilteredContactInput';

const InvoiceEditTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <span className="text-xl font-bold tracking-tight">Edit Invoice {record.reference}</span>
    );
};

const InvoiceEditBreadcrumb = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <Breadcrumb>
            <Breadcrumb.Item><Link to="/customer_invoices">Invoices</Link></Breadcrumb.Item>
            <Breadcrumb.Item><Link to={`/customer_invoices/${record.id}/show`}>{record.reference}</Link></Breadcrumb.Item>
            <Breadcrumb.PageItem>Edit</Breadcrumb.PageItem>
        </Breadcrumb>
    );
};

export const InvoiceEdit = () => (
    <Edit disableBreadcrumb title={<InvoiceEditTitle />} mutationMode="pessimistic">
        <InvoiceEditBreadcrumb />
        <SimpleForm className="max-w-5xl mx-auto space-y-6 relative" validate={requireNonZeroTotal}>
            <PaymentTermsWatcher />
            
            {/* General Details Card */}
            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-muted/50">
                    <div className="flex items-center gap-2">
                        <ReceiptText className="w-5 h-5 text-foreground" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Invoice Details</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <TextInput source="reference" label="Tax Invoice No." disabled className="w-full opacity-70" helperText={false} />
                        <DateInput source="date" className="w-full" />
                        
                        <StatusSelectInput source="status" choices={[
                            { id: 'draft', name: 'Draft' },
                            { id: 'sent', name: 'Sent' },
                            { id: 'pending_approval', name: 'Pending Approval' },
                            { id: 'cancelled', name: 'Cancelled' },
                            { id: 'revised', name: 'Revised' },
                        ]} className="w-full" />

                        <SelectInput source="payment_status" choices={[
                            { id: 'unpaid', name: 'Unpaid' },
                            { id: 'partial', name: 'Partially Paid' },
                            { id: 'paid', name: 'Fully Paid' },
                            { id: 'overdue', name: 'Overdue' },
                        ]} className="w-full" />

                        <ReferenceInput source="company_id" reference="companies">
                            <AutocompleteInput label="Company" className="w-full lg:col-span-2" />
                        </ReferenceInput>
                        <FilteredContactInput source="customer_id" label="Customer Contact" className="w-full lg:col-span-2" />

                        <ReferenceInput source="sales_order_id" reference="sales_orders">
                            <AutocompleteInput label="Related Sales Order" className="w-full lg:col-span-2" />
                        </ReferenceInput>

                        <DateInput source="due_date" label="Due Date" className="w-full" />
                        <NumberInput source="payment_term_days" label="Payment Term (Days)" className="w-full" />
                    </div>
                </CardContent>
            </Card>

            {/* Logistics (LR Details) Card */}
            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b">
                    <div className="flex items-center gap-2">
                        <Truck className="w-5 h-5 text-muted-foreground" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Logistics (LR Details)</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextInput source="lr_number" label="LR Number" className="w-full" />
                        <TextInput source="transporter_name" label="Transporter Name" className="w-full" />
                        <NumberInput source="lr_amount" label="Freight (LR) Amount" className="w-full" />
                    </div>
                </CardContent>
            </Card>

            {/* Line Items & Financials Card */}
            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b">
                    <div className="flex items-center gap-2">
                        <Landmark className="w-5 h-5 text-primary" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Line Items & Financials</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                    <DocumentItemsTable />
                    <GrandTotalsWatcher />
                    <RevisedReferenceWatcher />

                    {/* Tax summary */}
                    <div className="bg-muted p-4 rounded-lg border space-y-4">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Tax & Total Breakdown
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                            <NumberInput source="cgst_amount" label="CGST (₹)" readOnly className="w-full" />
                            <NumberInput source="sgst_amount" label="SGST (₹)" readOnly className="w-full" />
                            <NumberInput source="igst_amount" label="IGST (₹)" readOnly className="w-full" />
                            <NumberInput source="total_amount" label="Grand Total (₹)" readOnly className="w-full font-bold text-primary" />
                        </div>
                        <TextInput source="amount_in_words" label="Amount in Words" className="w-full italic" readOnly helperText="Auto-generated from Total" />
                    </div>
                </CardContent>
            </Card>

            {/* Payment & Bank Details Card */}
            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b">
                    <div className="flex items-center gap-2">
                        <WalletCards className="w-5 h-5 text-emerald-500" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Payment & Bank Details</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TextInput source="payment_terms" label="Payment Terms" multiline rows={3} className="w-full max-w-none" />
                        <TextInput source="bank_details" label="Bank Details Override" multiline rows={3} className="w-full max-w-none" />
                    </div>
                </CardContent>
            </Card>

            {/* Terms and Notes Card */}
            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-muted/50">
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Terms & Conditions</CardTitle>
                        </div>
                        <TermsTemplateSelector type="invoice" />
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <TextInput source="terms_conditions" label="Terms & Conditions" multiline rows={4} className="w-full max-w-none" />
                </CardContent>
            </Card>

        </SimpleForm>
    </Edit>
);
